import { useState } from "react";
import { ChevronDown, ChevronUp, MessageSquare, Brain, DollarSign, Shield, Zap, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      icon: <Brain className="h-5 w-5 text-primary" />,
      question: "What exactly is a Posit?",
      answer: "A Posit is your AI-powered Digital You—an \"another you\" that contains your specialized knowledge, expertise, and insights. Unlike generic AI, your Posit provides niche advice that only you can offer, helping people access your expertise 24/7 even when you're not available.",
      highlight: "Create your Digital You"
    },
    {
      icon: <DollarSign className="h-5 w-5 text-green-600" />,
      question: "How can I make money with Posit?",
      answer: "Set your own monthly subscription price for paid Posits. Free users keep 80% of revenue, Posit+ users keep 90%. You can also join our affiliate program and earn 5% commission on referrals for one year. Perfect for consultants, coaches, educators, or any expert looking to create passive income from their knowledge.",
      highlight: "Keep 80-90% of your revenue"
    },
    {
      icon: <Zap className="h-5 w-5 text-yellow-600" />,
      question: "What makes Posit different from ChatGPT or other AI?",
      answer: "While ChatGPT provides general knowledge, Posit gives you specialized expertise from real professionals. Each Posit contains curated knowledge from domain experts, offering niche insights that generic AI simply can't provide. Plus, creators get paid for sharing their expertise, creating a sustainable ecosystem of specialized knowledge.",
      highlight: "Specialized expertise, not generic responses"
    },
    {
      icon: <MessageSquare className="h-5 w-5 text-blue-600" />,
      question: "How easy is it to create and update my Posit?",
      answer: "Very easy! Just chat with our AI about your expertise - share experiences, insights, and processes. The AI automatically structures and organizes everything for you. You can update anytime through voice, text, or document uploads.",
      highlight: "Natural conversation builds your expertise"
    },
    {
      icon: <Users className="h-5 w-5 text-purple-600" />,
      question: "Who can benefit from using Posit?",
      answer: "Anyone with valuable knowledge! Consultants, coaches, educators, technical experts, creative professionals, and industry specialists. Whether you want to scale your expertise, create passive income, or help more people access your knowledge, Posit helps you share what you know.",
      highlight: "Perfect for experts and knowledge workers"
    },
    {
      icon: <Shield className="h-5 w-5 text-indigo-600" />,
      question: "Can I control who accesses my Posit?",
      answer: "Yes! You have three privacy levels: Public (discoverable by anyone), Private (only you can access), or Exclusive (share with specific people via email). Posit+ users get access to private and exclusive options.",
      highlight: "Full privacy control - your content, your rules"
    },
    {
      icon: <Users className="h-5 w-5 text-emerald-600" />,
      question: "How does the affiliate program work?",
      answer: "When you refer someone who subscribes to Posit+, you earn 5% commission on their subscription for one year. It's perfect for people who believe in Posit and want to help others discover it while earning passive income. You're not just making a sale - you're sharing a tool that helps people monetize their expertise.",
      highlight: "Earn 5% commission for one year"
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="container mx-auto px-4 py-16 md:py-24">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Frequently Asked Questions
          </h2>

        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <Card key={index} className="border border-border/50 hover:border-primary/20 transition-colors">
              <CardContent className="p-0">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full p-6 text-left flex items-center justify-between hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {faq.icon}
                    <div>
                      <h3 className="font-semibold text-lg">{faq.question}</h3>
                      <p className="text-sm text-primary font-medium">{faq.highlight}</p>
                    </div>
                  </div>
                  {openIndex === index ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  )}
                </button>
                
                {openIndex === index && (
                  <div className="px-6 pb-6">
                    <div className="pl-8 border-l-2 border-primary/20">
                      <p className="text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>


      </div>
    </section>
  );
};

export default FAQSection;